class ProductCardAudio {
  constructor(options = {}) {
    // 配置选项
    this.options = {
      toastDuration: options.toastDuration || 3000, // Toast显示时长
      debug: options.debug || false, // 调试模式
    };

    this.currentAudio = null;
    this.currentButton = null;
    this.currentAudioUrl = null;
    this.init();
  }

  init() {
    // 创建全局Toast容器
    this.createToastContainer();

    // 绑定事件 - 如果DOM已加载则立即绑定，否则等待DOMContentLoaded
    if (document.readyState === "loading") {
      document.addEventListener("DOMContentLoaded", () => {
        this.bindEvents();
      });
    } else {
      this.bindEvents();
    }
  }

  createToastContainer() {
    // 避免重复创建
    if (document.getElementById("audio-toast-container")) return;

    const toastContainer = document.createElement("div");
    toastContainer.id = "audio-toast-container";
    toastContainer.style.cssText = `
        position: fixed;
        bottom: 20px;
        left: 50%;
        transform: translateX(-50%);
        z-index: 9999;
        display: flex;
        flex-direction: column;
        align-items: center;
        gap: 10px;
      `;
    document.body.appendChild(toastContainer);
  }

  showToast(message, type = "error") {
    const toast = document.createElement("div");
    toast.className = `audio-toast ${type}`;
    toast.textContent = message;
    toast.style.cssText = `
        padding: 12px 24px;
        width: 35rem;
        background: rgba(0, 0, 0, 0.8);
        color: white;
        border-radius: 1.2rem;
        animation: fadeIn 0.3s, fadeOut 0.3s ${
          this.options.toastDuration / 1000 - 0.3
        }s;
      `;

    // 添加CSS动画
    const style = document.createElement("style");
    style.textContent = `
        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(20px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes fadeOut {
          to { opacity: 0; transform: translateY(-20px); }
        }
      `;
    document.head.appendChild(style);

    const container = document.getElementById("audio-toast-container");
    container.appendChild(toast);

    // 自动移除
    setTimeout(() => {
      toast.remove();
      style.remove();
    }, this.options.toastDuration);
  }

  bindEvents() {
    const audioButtons = document.querySelectorAll(".product-card__audio-btn");
    audioButtons.forEach((button) => {
      button.addEventListener("click", (e) => {
        e.preventDefault();
        this.handleAudioClick(button);
      });
    });

    // 全局音频播放管理
    document.addEventListener(
      "play",
      (e) => {
        if (e.target.tagName === "AUDIO" && e.target !== this.currentAudio) {
          this.stopAudio();
        }
      },
      true
    );
  }

  handleAudioClick(button) {
    const audioUrl = button.dataset.audioUrl;

    if (!audioUrl) {
      this.log("No audio URL provided for this button");
      return;
    }

    // 情况1: 同一个按钮且正在播放 → 暂停
    if (this.isSameButtonAndPlaying(button)) {
      this.pauseAudio();
      return;
    }

    // 情况2: 同一个按钮且已暂停 → 继续播放
    if (this.isSameButtonAndPaused(button)) {
      this.resumeAudio();
      return;
    }

    // 情况3: 不同按钮或音频已完成 → 重新播放
    this.playNewAudio(audioUrl, button);
  }

  isSameButtonAndPlaying(button) {
    return (
      this.currentButton === button &&
      this.currentAudio &&
      !this.currentAudio.paused
    );
  }

  isSameButtonAndPaused(button) {
    return (
      this.currentButton === button &&
      this.currentAudio &&
      this.currentAudio.paused
    );
  }

  playNewAudio(url, button) {
    // 停止当前播放
    this.stopAudio();

    // 记录当前音频URL
    this.currentAudioUrl = url;

    // 创建新的Audio对象
    this.currentAudio = new Audio(url);
    this.currentButton = button;

    // 更新按钮状态
    button.classList.add("playing");

    // 事件监听
    this.currentAudio.addEventListener("ended", this.handleAudioEnd.bind(this));
    this.currentAudio.addEventListener(
      "error",
      this.handleAudioError.bind(this)
    );

    // 尝试播放
    this.currentAudio
      .play()
      .then(() => {
        this.log("Audio playback started successfully");
      })
      .catch((error) => {
        this.handleAudioError(error, false);
      });
  }

  pauseAudio() {
    if (this.currentAudio) {
      this.currentAudio.pause();
      this.currentButton.classList.remove("playing");
      this.currentButton.classList.add("paused");
      this.log("Audio paused");
    }
  }

  resumeAudio() {
    if (this.currentAudio && this.currentAudio.paused) {
      this.currentAudio
        .play()
        .then(() => {
          this.currentButton.classList.add("playing");
          this.currentButton.classList.remove("paused");
          this.log("Audio resumed");
        })
        .catch((error) => {
          this.handleAudioError(error);
        });
    }
  }

  handleAudioEnd() {
    this.log("Audio playback completed");
    this.resetButtonState(this.currentButton);
    this.currentAudio = null;
    this.currentButton = null;
    this.currentAudioUrl = null;
  }

  handleAudioError(error, showToast = true) {
    console.error("Audio playback error:", error);
    if (showToast) {
      this.showToast(
        "Something went wrong, the audio failed to play as expected.",
        "error"
      );
    }
    this.resetButtonState(this.currentButton);
    this.currentAudio = null;
    this.currentButton = null;
    this.currentAudioUrl = null;
  }

  stopAudio() {
    if (this.currentAudio) {
      this.currentAudio.pause();
      this.currentAudio.currentTime = 0;
      this.currentAudio.removeEventListener("ended", this.handleAudioEnd);
      this.currentAudio.removeEventListener("error", this.handleAudioError);
    }

    if (this.currentButton) {
      this.resetButtonState(this.currentButton);
    }

    this.currentAudio = null;
    this.currentButton = null;
    this.currentAudioUrl = null;
  }

  resetButtonState(button) {
    if (button) {
      button.classList.remove("playing", "paused");
    }
  }

  log(message) {
    if (this.options.debug) {
      console.log(`[ProductCardAudio] ${message}`);
    }
  }
}

// 初始化音频控制器（单例模式）
if (typeof window.ProductCardAudioInstance === "undefined") {
  window.ProductCardAudioInstance = new ProductCardAudio({
    toastDuration: 3000, // Toast显示3秒
    debug: true, // 开启调试模式
  });
}
